// Types mirroring ecregi-api DTOs. Keep in sync with /ecregi-api/src/modules/**/dto/*.ts
// (eventually generate from OpenAPI at /v1/docs).

export type Uuid = string;
export type IsoDate = string;
export type BigIntStr = string; // BigInt serialized as string (Prisma money → JSON)
export type DecimalStr = string; // Prisma Decimal serialized as string

// ---- User / auth -------------------------------------------
export type UserRole = 'CLIENT' | 'ASSOCIATE' | 'FRANCHISE' | 'ADMIN';
export type UserStatus =
  | 'PENDING_EMAIL_VERIFICATION'
  | 'PENDING_ADMIN_APPROVAL'
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'REJECTED';

export interface PublicUser {
  id: Uuid;
  email: string;
  role: UserRole;
  adminRole?: AdminRole | null;
  status: UserStatus;
  firstName: string | null;
  lastName: string | null;
  mobile: string | null;
  rejectionReason?: string | null;
  profilePictureS3Key: string | null;
}

export interface MyProfile extends PublicUser {
  avatarUrl: string | null;
  emailVerifiedAt: IsoDate | null;
  lastLoginAt: IsoDate | null;
  createdAt: IsoDate;
}

export interface IssuedTokens {
  accessToken?: string;
  refreshToken?: string;
  accessTokenTtl?: string;
  refreshTokenTtl?: string;
}

export interface AuthSession {
  accessToken?: string;
  refreshToken?: string;
  accessTokenTtl?: string;
  refreshTokenTtl?: string;
  user: PublicUser;
}

export type VerifyOtpResponse =
  | ({ status: 'LOGGED_IN' } & AuthSession)
  | { status: 'SIGNUP_REQUIRED'; signupToken: string; signupTokenTtl: string };

// ---- Services ----------------------------------------------
// Fixed catalogue buckets — mirrors the Postgres ServiceCategory enum.
// Update SERVICE_CATEGORIES + SERVICE_CATEGORY_LABELS in tandem.
export type ServiceCategory =
  | 'DOCUMENTATION'
  | 'BUSINESS_REGISTRATION'
  | 'COMPLIANCE'
  | 'FINANCE'
  | 'LEGAL';

export const SERVICE_CATEGORIES: readonly ServiceCategory[] = [
  'DOCUMENTATION',
  'BUSINESS_REGISTRATION',
  'COMPLIANCE',
  'FINANCE',
  'LEGAL',
] as const;

export const SERVICE_CATEGORY_LABELS: Record<ServiceCategory, string> = {
  DOCUMENTATION: 'Documentation',
  BUSINESS_REGISTRATION: 'Business Registration',
  COMPLIANCE: 'Compliance',
  FINANCE: 'Finance',
  LEGAL: 'Legal',
};

// Hard-coded presets shown in the admin create dialog before the server
// responds. The server merges these with any custom admin-entered values at
// /services/sub-categories, but having them here avoids dropdown flash on
// first paint. Keep this in lockstep with DEFAULT_SUB_CATEGORIES in
// ecregi-api/src/modules/services/dto/services.dto.ts.
export const DEFAULT_SUB_CATEGORIES: Record<ServiceCategory, string[]> = {
  DOCUMENTATION: ['Property', 'Business', 'Others'],
  BUSINESS_REGISTRATION: ['Company Registration', 'License Registration'],
  COMPLIANCE: ['GST', 'Income Tax', 'Others'],
  FINANCE: [],
  LEGAL: ['Support Services', 'Specialization'],
};

// /services/sub-categories response shape.
export interface SubCategoriesByCategory {
  byCategory: Record<ServiceCategory, string[]>;
}

// Pre-questionnaire attached to a service. OPTIONS questions are answered
// in the start dialog; TEXT questions are asked by the system inside the
// chat thread once the transaction opens.
export type QuestionnaireAnswerType = 'TEXT' | 'OPTIONS';

export interface QuestionnaireQuestion {
  id: string;
  label: string;
  type: QuestionnaireAnswerType;
  options: string[];
  required: boolean;
  order: number;
}

export interface ServiceSummary {
  id: Uuid;
  name: string;
  category: ServiceCategory;
  authorityServiceCode?: string | null;
  authority?: {
    id: Uuid;
    code: string | null;
    name: string;
    images?: string[];
    createdAt?: IsoDate | Date | null;
  } | null;
  subCategory: string | null;
  description: string | null;
  timelineText: string | null;
  associateGstPercentage?: DecimalStr;
  associateHsnSacCode?: string;
  associateGstAmountRupees?: DecimalStr;
  platformGstPercentage?: DecimalStr;
  platformHsnSacCode?: string;
  platformGstAmountRupees?: DecimalStr;
  hsnSacCode?: string;
  gstPercentage: DecimalStr;
  defaultAssociateFeeRupees?: DecimalStr;
  defaultPlatformFeeRupees?: DecimalStr;
  governmentFeeRupees?: DecimalStr;
  defaultApplicationFeeRupees?: DecimalStr;
  defaultServiceChargeRupees?: DecimalStr;
  totalAssociateFeeRupees?: DecimalStr;
  totalPlatformFeeRupees?: DecimalStr;
  grandTotalRupees?: DecimalStr;
  isActive: boolean;
  isStartsFrom?: boolean;
  startsFromRupees?: string | null;
  images: string[];
  questionnaire: QuestionnaireQuestion[];
  requiredDocuments: string[];
  sortOrder: number;
  stageRefundPolicy?: unknown;
  additionalCharges?: AdditionalChargeItem[];
}

export interface SubServiceSummary {
  id: Uuid;
  serviceId: Uuid;
  name: string;
  subCategory: string | null;
  defaultApplicationFeeRupees: DecimalStr | null;
  defaultServiceChargeRupees: DecimalStr | null;
  isActive: boolean;
}

export interface ServiceDetail extends ServiceSummary {
  defaultAssociateSplitPercentage: DecimalStr;
  stageRefundPolicy: unknown;
  subServices: SubServiceSummary[];
}

export interface ServiceListPage {
  items: ServiceSummary[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface CategoryFacet {
  category: ServiceCategory;
  count: number;
}

// Powers the landing mega-menu. Two-level grouping: each of the 5 fixed
// ServiceCategory buckets carries a list of sub-groups keyed by
// sub_services.sub_category. Services without sub-services surface under a
// `General` sub-group so every active service is reachable from the menu.
export const GENERAL_SUB_CATEGORY = 'General';

export interface GroupedServiceItem {
  id: Uuid; // sub-service id; falls back to the service id when the service has no sub-services
  name: string;
  serviceId: Uuid; // always the parent service id — used for deep linking
  timelineText: string | null;
}
export interface SubCategoryGroup {
  subCategory: string;
  items: GroupedServiceItem[];
}
export interface ServiceCategoryGroup {
  category: ServiceCategory;
  subGroups: SubCategoryGroup[];
}
export interface GroupedServices {
  groups: ServiceCategoryGroup[];
}

// ---- Notifications -----------------------------------------
export interface Notification {
  id: Uuid;
  userId: Uuid;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: IsoDate | null;
  createdAt: IsoDate;
}

export interface NotificationPage {
  items: Notification[];
  unreadCount: number;
  nextCursor: Uuid | null;
  hasMore: boolean;
}

// ---- Transactions ------------------------------------------
export type AssociateAssignmentStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED';
export type AssociateVerificationStatus =
  | 'NOT_APPLICABLE'
  | 'PENDING_CLIENT_VERIFICATION'
  | 'VERIFIED_BY_CLIENT'
  | 'REJECTED_BY_CLIENT';
export type CancellationRequestStatus = 'NONE' | 'PENDING' | 'ACCEPTED' | 'REJECTED';

export type TransactionStatus =
  | 'SERVICE_REQUESTED'
  | 'CHAT_CREATED'
  | 'ASSOCIATE_ASSIGNMENT_PENDING'
  | 'ASSOCIATE_ASSIGNED'
  | 'ASSOCIATE_ACCEPTANCE_PENDING'
  | 'ASSOCIATE_ACCEPTED'
  | 'QUOTATION_REVIEW_PENDING'
  | 'CLIENT_CONFIRMATION_PENDING'
  | 'CLIENT_CONFIRMED'
  | 'PAYMENT_PENDING'
  | 'INITIATED'
  | 'INVOICE_CREATED'
  | 'PAID'
  | 'PAYMENT_RECEIVED'
  | 'ASSIGNED'
  | 'ASSIGNED_REVOKED'
  | 'IN_PROGRESS'
  | 'DRAFT_PREPARATION'
  | 'CLIENT_DRAFT_CONFIRMATION'
  | 'REVIEW'
  | 'REVISION_REQUESTED'
  | 'SENT_FOR_APPROVAL'
  | 'APPROVED'
  | 'FINAL_RESULT_RECEIVED'
  | 'DELIVERED'
  | 'FINAL_DOC_READY'
  | 'GRIEVANCE_RAISED'
  | 'CLIENT_CONFIRMED_FINAL'
  | 'RATING_PENDING'
  | 'SETTLEMENT_PENDING'
  | 'SETTLED'
  | 'COMPLETE'
  | 'CLOSED'
  | 'CANCELLED';

export interface ServiceMinimal {
  id: Uuid;
  name: string;
  category: ServiceCategory;
  images: string[];
  requiredDocuments: string[];
  defaultAssociateFeeRupees?: string;
  defaultPlatformFeeRupees?: string;
  defaultApplicationFeeRupees?: string;
  defaultServiceChargeRupees?: string;
  governmentFeeRupees?: string;
  totalAssociateFeeRupees?: string;
  totalPlatformFeeRupees?: string;
  grandTotalRupees?: string;
  additionalCharges?: unknown;
  stageRefundPolicy?: unknown;
  isStartsFrom?: boolean;
  startsFromRupees?: string | null;
}
export interface SubServiceMinimal {
  id: Uuid;
  name: string;
  subCategory: string | null;
}

export interface CandidateAssociateStatus {
  id: Uuid;
  firstName: string | null;
  lastName: string | null;
  email?: string | null;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';
  assignedAt?: IsoDate | null;
}

export interface TransactionListItem {
  id: Uuid;
  code: string;
  clientId: Uuid;
  serviceId: Uuid;
  subServiceId: Uuid | null;
  assignedAssociateId: Uuid | null;
  candidateAssociateIds?: Uuid[];
  candidateAssignedAt?: Record<string, string>;
  candidateAssociates?: CandidateAssociateStatus[];
  associateAssignmentStatus?: AssociateAssignmentStatus | null;
  associateVerificationStatus?: AssociateVerificationStatus | null;
  associateRejectionCount?: number;
  cancellationRequestStatus?: CancellationRequestStatus | null;
  cancellationRequestReason?: string | null;
  cancellationRequestedAt?: IsoDate | null;
  status: TransactionStatus;
  initiatedAt: IsoDate;
  paidAt: IsoDate | null;
  assignedAt?: IsoDate | null;
  assignmentExpiresAt?: IsoDate | null;
  isAssignmentExpired?: boolean;
  updatedAt: IsoDate;
  service: ServiceMinimal;
  subService: SubServiceMinimal | null;
  lastMessageAt: IsoDate | null;
  unreadCount: number;
  nickName?: string | null;
  narration?: string | null;
  rating?: number | null;
  feedbackDescription?: string | null;
}

export interface SectionCounts {
  active: number;
  completed: number;
  cancelled: number;
  declined: number;
  all: number;
}

export interface TransactionListPage {
  items: TransactionListItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  counts?: SectionCounts;
}

export type MessageType =
  | 'TEXT'
  | 'DOCUMENT'
  | 'SYSTEM_EVENT'
  | 'INVOICE'
  | 'DRAFT'
  | 'FINAL_DOC'
  | 'SCOPE_FORM';

export type ScopeFormStatus = 'PROPOSED' | 'FINALIZED' | 'ACCEPTED' | 'REJECTED';

export interface ScopeFormResponse {
  id: Uuid;
  transactionId: Uuid;
  serviceDescription: string;
  workScope: string;
  deliverables: string;
  professionalFeeRupees: DecimalStr;
  expectedTat: string;
  governmentFeeRupees: DecimalStr;
  additionalRequirements: string | null;
  exclusions: string | null;
  specialConditions: string | null;
  platformFeeRupees: DecimalStr | null;
  totalRupees: DecimalStr;
  status: ScopeFormStatus;
  createdByAssociateId: Uuid;
  finalizedByAdminId: Uuid | null;
  acceptedAt: IsoDate | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export interface CreateScopeFormInput {
  serviceDescription: string;
  workScope: string;
  deliverables: string;
  professionalFeeRupees: string;
  expectedTat: string;
  governmentFeeRupees?: string;
  additionalRequirements?: string;
  exclusions?: string;
  specialConditions?: string;
}

export interface AdminUpdateScopeFormInput {
  platformFeeRupees: string;
  serviceDescription?: string;
  workScope?: string;
  deliverables?: string;
  professionalFeeRupees?: string;
  expectedTat?: string;
  governmentFeeRupees?: string;
  additionalRequirements?: string;
  exclusions?: string;
  specialConditions?: string;
}

// Role-aware identity of the sender. Server is the source of truth for
// what's safe to render per viewer — see ChatSenderSchema in the API.
// ADMIN + SYSTEM are always anonymised; CLIENT/ASSOCIATE/FRANCHISE carry
// just the first name + avatar + (for associates) a short opaque handle.
export type ChatSenderRole = 'CLIENT' | 'ASSOCIATE' | 'FRANCHISE' | 'ADMIN' | 'SYSTEM';
export interface ChatSender {
  role: ChatSenderRole;
  firstName: string | null;
  lastName?: string | null;
  adminRole?: AdminRole | null;
  avatarUrl: string | null;
  associateId: string | null;
}

export interface ChatMessage {
  id: Uuid;
  threadId: Uuid;
  transactionId: Uuid;
  senderUserId: Uuid | null;
  sender: ChatSender;
  type: MessageType;
  body: string | null;
  attachmentDocumentId: Uuid | null;
  metadata: Record<string, unknown> | null;
  createdAt: IsoDate;
  // Server-stamped when the message has been accepted and broadcast.
  // null on legacy rows or while a message is still in flight — drives
  // the single-vs-double tick state on own messages.
  deliveredAt: IsoDate | null;
}

export interface ChatHistoryPage {
  items: ChatMessage[];
  nextCursor: Uuid | null;
  hasMore: boolean;
}

export interface ReadReceipt {
  threadId: Uuid;
  userId: Uuid;
  lastReadMessageId: Uuid | null;
  lastReadAt: IsoDate | null;
}

// ---- Transaction detail ------------------------------------
// No email / mobile here — the public /transactions/:id is used by clients
// and associates, and cross-party contact info doesn't belong on that
// surface. The admin console fetches the richer shape via its own routes.
export interface PartySummary {
  id: Uuid;
  firstName: string | null;
  lastName: string | null;
}

export interface TransactionDetail extends TransactionListItem {
  questionnaireAnswers: Record<string, unknown>;
  refundPolicySnapshot: unknown;
  platformFeePercentageSnapshot: DecimalStr;
  client: PartySummary | null;
  assignedAssociate: PartySummary | null;
  cancellationReason: string | null;
}

// ---- Documents ---------------------------------------------
export type DocumentKind = 'SUPPORTING' | 'DRAFT' | 'FINAL' | 'REFERENCE';
export type DocumentOwner = 'CLIENT' | 'ASSOCIATE' | 'ADMIN' | 'SYSTEM';

export interface DocumentResponse {
  id: Uuid;
  transactionId: Uuid;
  uploadedByUserId: Uuid | null;
  owner: DocumentOwner;
  kind: DocumentKind;
  fileName: string;
  mimeType: string;
  sizeBytes: BigIntStr;
  isDraft: boolean;
  isFinal: boolean;
  draftVersion: number | null;
  archivedAt: IsoDate | null;
  createdAt: IsoDate;
}

export interface DocumentUploadUrlResponse {
  uploadUrl: string;
  key: string;
  bucket: string;
  expiresInSeconds: number;
  expiresAt: IsoDate;
  headers: Record<string, string>;
}

export interface DocumentDownloadResponse {
  url: string;
  key: string;
  expiresInSeconds: number;
  expiresAt: IsoDate;
}

export interface WalletDocumentResponse {
  id: Uuid;
  userId: Uuid;
  documentName: string;
  documentType: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: BigIntStr;
  s3Key: string;
  downloadUrl?: string | null;
  archivedAt: IsoDate | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

// ---- Payments ----------------------------------------------
export interface PaymentOrderResponse {
  providerOrderId: string;
  keyId: string;
  amountPaise: BigIntStr;
  currency: string;
  invoice: { id: Uuid; invoiceNumber: string; totalPaise: BigIntStr };
  prefill: {
    name: string | null;
    email: string | null;
    contact: string | null;
  };
}

export interface PaymentResponse {
  id: Uuid;
  invoiceId: Uuid;
  transactionId: Uuid;
  provider: string;
  providerOrderId: string | null;
  providerPaymentId: string | null;
  amountPaise: BigIntStr;
  status: string;
  paidAt: IsoDate | null;
  refundedAt: IsoDate | null;
  refundAmountPaise: BigIntStr;
  createdAt: IsoDate;
}

// ---- Associate ---------------------------------------------
export type Profession =
  | 'LAWYER'
  | 'CHARTERED_ACCOUNTANT'
  | 'COMPANY_SECRETARY'
  | 'COST_MANAGEMENT_ACCOUNTANT'
  | 'DOCUMENT_WRITER'
  | 'TAX_CONSULTANT'
  | 'OTHER_DEGREE_HOLDER';

export type KycStatus = 'NOT_SUBMITTED' | 'PENDING' | 'VERIFIED' | 'REJECTED';

export interface AssociateAddress {
  pincode: string | null;
  state: string | null;
  district: string | null;
  taluk: string | null;
  village: string | null;
  landmark: string | null;
  street: string | null;
}

export interface AssociateProfile {
  userId: Uuid;
  associateId: string | null;
  profession: Profession;
  registrationNo: string | null;
  isFranchise: boolean;
  outletName: string | null;
  address: AssociateAddress;
  kycStatus: KycStatus;
  kycSubmittedAt: IsoDate | null;
  kycVerifiedAt: IsoDate | null;
  averageRating?: number | null;
  ratingCount?: number;
}

export interface AssociateFeedback {
  transactionId: Uuid;
  transactionCode: string;
  serviceName: string;
  clientName: string;
  rating: number;
  feedbackDescription: string | null;
  completedAt: IsoDate;
}

export interface AssociateFeedbackStats {
  total: number;
  average: number;
  distribution: Record<number, number>;
  positiveRate: number;
  fiveStarCount: number;
}

export interface AssociateFeedbacksQuery {
  search?: string;
  rating?: number;
  sortBy?: 'newest' | 'oldest' | 'highest' | 'lowest';
  page?: number;
  pageSize?: number;
}

export interface AssociateFeedbacksResponse {
  items: AssociateFeedback[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  stats: AssociateFeedbackStats;
}

export interface AssociatePublicProfileReviewItem {
  clientName: string;
  rating: number;
  feedbackDescription: string | null;
  completedAt: IsoDate | null;
}

export interface AssociatePublicProfileResponse {
  userId: Uuid;
  associateId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  profession: Profession | null;
  profilePictureUrl: string | null;
  associateRejectionCount: number;
  associateVerificationStatus: AssociateVerificationStatus | null;
  rating: {
    average: number;
    totalReviews: number;
    starCounts: Record<number, number>;
  };
  reviews: AssociatePublicProfileReviewItem[];
}

export interface KycStatusResponse {
  status: KycStatus;
  submittedAt: IsoDate | null;
  verifiedAt: IsoDate | null;
  rejectionReason: string | null;
  aadhaarLast4: string | null;
  panNumber: string | null;
  bankAccountLast4: string | null;
  bankName: string | null;
  bankBranch: string | null;
  ifscCode: string | null;
  gstin: string | null;
}

export interface AssociateAnalytics {
  totalsByStatus: Partial<Record<TransactionStatus, number>>;
  assignedAllTime: number;
  pendingAllTime: number;
  assignedOpen: number;
  completedThisMonth: number;
  completedAllTime: number;
  lifetimePayoutPaise: BigIntStr;
  pendingPayoutPaise: BigIntStr;
  momDelta: {
    assigned: number;
    completed: number;
    pending: number;
    earningsPaise: number;
  };
}

export interface AnalyticsMonth {
  month: number;
  completed: number;
  pending: number;
  earningsPaise: BigIntStr;
}

export interface AnalyticsSeriesResponse {
  year: number;
  months: AnalyticsMonth[];
}

export interface AssociateServiceItem {
  id: Uuid;
  name: string;
  category: string;
  hsnSacCode: string;
  isActive: boolean;
}

export type KycDocType = 'AADHAAR' | 'PAN';

export interface KycDocUploadUrlResponse {
  uploadUrl: string;
  key: string;
  bucket: string;
  expiresInSeconds: number;
  expiresAt: IsoDate;
  headers: Record<string, string>;
}

// ============================================================
// Admin surfaces (Phase 3)
// ============================================================

export type AdminRole =
  | 'SUPER_ADMIN'
  | 'OH_SERVICE_MANAGER'
  | 'ASSIGNER'
  | 'CASE_EXECUTIVE'
  | 'ASSOCIATE_CONTROLLING_EXECUTIVE'
  | 'CUSTOMER_SUPPORT_EXECUTIVE'
  | 'FINANCE_EXECUTIVE'
  | 'AUDITOR_COMPLIANCE_REVIEWER'
  | 'OPS_ADMIN'
  | 'READ_ONLY_ADMIN'
  | 'ASSIGNER_ADMIN';

export const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  OH_SERVICE_MANAGER: 'OH / Service Manager',
  ASSIGNER: 'Assigner',
  CASE_EXECUTIVE: 'Case Executive',
  ASSOCIATE_CONTROLLING_EXECUTIVE: 'Associate Controlling Executive',
  CUSTOMER_SUPPORT_EXECUTIVE: 'Customer Support Executive',
  FINANCE_EXECUTIVE: 'Finance Executive',
  AUDITOR_COMPLIANCE_REVIEWER: 'Auditor / Compliance Reviewer',
  OPS_ADMIN: 'Ops Admin',
  READ_ONLY_ADMIN: 'Read Only Admin',
  ASSIGNER_ADMIN: 'Assigner Admin',
};

export function formatAdminRole(role?: AdminRole | string | null): string {
  if (!role) return 'Admin';
  return (ADMIN_ROLE_LABELS as Record<string, string>)[role] || role;
}

export interface AdminVerificationUserRow {
  id: Uuid;
  email: string;
  firstName: string | null;
  lastName: string | null;
  mobile: string | null;
  adminRole: AdminRole | null;
  status: UserStatus;
  rejectionReason: string | null;
  reviewedByAdminId: string | null;
  reviewedAt: IsoDate | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export type AdminSection =
  | 'TRANSACTIONS'
  | 'KYC'
  | 'USERS'
  | 'INVOICES'
  | 'PAYOUTS'
  | 'REFUNDS'
  | 'AUTHORITIES'
  | 'SERVICES'
  | 'CONFIG'
  | 'REPORTS'
  | 'FEEDBACKS'
  | 'AUDIT_LOGS';

export interface SectionPermissionItem {
  section: AdminSection;
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export interface AdminUserWithPermissionsRow {
  id: Uuid;
  email: string;
  firstName: string | null;
  lastName: string | null;
  mobile: string | null;
  adminRole: AdminRole | null;
  status: UserStatus;
  createdAt: IsoDate;
  updatedAt: IsoDate;
  permissions: SectionPermissionItem[];
}

// ---- Admin — Users -----------------------------------------
export interface AdminUserRow {
  id: Uuid;
  email: string;
  role: UserRole;
  adminRole: AdminRole | null;
  status: UserStatus;
  firstName: string | null;
  lastName: string | null;
  mobile: string | null;
  emailVerifiedAt: IsoDate | null;
  lastLoginAt: IsoDate | null;
  createdAt: IsoDate;
  deletedAt: IsoDate | null;
  associateProfile?: {
    profession: string;
    outletName: string | null;
    isFranchise: boolean;
    kycStatus: string;
    pincode: string | null;
    state: string | null;
    district: string | null;
    taluk: string | null;
    village: string | null;
    landmark: string | null;
    street: string | null;
    associateId?: string | null;
  } | null;
}

export interface AdminUserPage {
  items: AdminUserRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// ---- Admin — Transactions ---------------------------------
// Admin-only party summary: includes email because admins legitimately need
// it for support + reconciliation. Deliberately distinct from the public
// `PartySummary` so a refactor of the public surface can never accidentally
// re-expose email back to clients/associates.
export interface AdminPartySummary {
  id: Uuid;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export interface AdminTransactionRow extends TransactionListItem {
  client: AdminPartySummary | null;
  assignedAssociate: AdminPartySummary | null;
  cancellationReason: string | null;
  completedAt: IsoDate | null;
  cancelledAt: IsoDate | null;
  finalDocReadyAt: IsoDate | null;
  assignedAt: IsoDate | null;
  approvedAt: IsoDate | null;
  // Only populated by the /admin/transactions/:id detail endpoint (see
  // AdminTransactionsService.getOne). The list endpoint returns these rows
  // without the nested relations, so downstream consumers must null-check.
  // Used on the admin tx detail page to decide whether to offer
  // "Create invoice" (status = INITIATED) or "Mark paid offline" (status =
  // INVOICE_CREATED and the latest invoice's paymentMode is OFFLINE).
  invoices?: InvoiceSummary[];
}

export interface AdminTransactionPage {
  items: AdminTransactionRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AdminStateHistoryEntry {
  id: Uuid;
  fromStatus: TransactionStatus | null;
  toStatus: TransactionStatus;
  changedByUserId: Uuid | null;
  reason: string | null;
  metadata: unknown;
  createdAt: IsoDate;
  actor: {
    id: Uuid;
    email: string;
    role: UserRole;
    adminRole: AdminRole | null;
  } | null;
}

// ---- Invoices ----------------------------------------------
export type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'CANCELLED' | 'REFUNDED';
export type InvoicePaymentMode = 'ONLINE' | 'OFFLINE';

export interface InvoiceSummary {
  id: Uuid;
  transactionId: Uuid;
  invoiceNumber: string;
  fiscalYear: string;
  serialNo: number;
  status: InvoiceStatus;
  paymentMode: InvoicePaymentMode;
  subtotalPaise: BigIntStr;
  gstPercentage: DecimalStr;
  gstPaise: BigIntStr;
  cgstPaise: BigIntStr;
  sgstPaise: BigIntStr;
  igstPaise: BigIntStr;
  totalPaise: BigIntStr;
  hsnSacCode: string;
  placeOfSupplyState: string;
  ecregiGstin: string;
  ecregiLegalName: string;
  clientGstin: string | null;
  platformFeePaise: BigIntStr;
  associateSplitPercentage: DecimalStr;
  associatePayoutPaise: BigIntStr;
  currency: string;
  pdfS3Key: string | null;
  sentAt: IsoDate | null;
  paidAt: IsoDate | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export interface InvoiceLineItem {
  id: Uuid;
  description: string;
  amountPaise: BigIntStr;
  quantity: number;
  sortOrder: number;
}

export interface InvoiceDetail extends InvoiceSummary {
  lineItems: InvoiceLineItem[];
}

export interface InvoiceListPage {
  items: InvoiceSummary[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// ---- Refunds (admin view) ---------------------------------
export type RefundStatus =
  | 'REQUESTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'INITIATED'
  | 'COMPLETED'
  | 'FAILED';

export interface AdminRefundRow {
  id: Uuid;
  transactionId: Uuid;
  invoiceId: Uuid;
  paymentId: Uuid | null;
  status: RefundStatus;
  cancelledFromStatus: string;
  paidAmountPaise: BigIntStr;
  platformFeePaise: BigIntStr;
  gatewayFeePaise: BigIntStr;
  stageRefundPercentage: DecimalStr;
  eligibleRefundPaise: BigIntStr;
  providerRefundId: string | null;
  rejectionReason: string | null;
  initiatedAt: IsoDate | null;
  completedAt: IsoDate | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export interface AdminRefundPage {
  items: AdminRefundRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// ---- Payouts (admin view) ---------------------------------
export type PayoutStatus = 'PENDING' | 'PAID' | 'ON_HOLD' | 'CANCELLED';

export interface AdminPayoutRow {
  id: Uuid;
  transactionId: Uuid;
  associateUserId: Uuid;
  grossAmountPaise: BigIntStr;
  tdsPercentage: DecimalStr;
  tdsAmountPaise: BigIntStr;
  netAmountPaise: BigIntStr;
  tdsSectionCode: string;
  associatePanSnapshot: string | null;
  status: PayoutStatus;
  paidAt: IsoDate | null;
  paidByAdminId: Uuid | null;
  transactionReference: string | null;
  notes: string | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export interface AdminPayoutPage {
  items: AdminPayoutRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// ---- Admin — Services (catalog admin) ---------------------
export interface AdminServiceRow {
  id: Uuid;
  name: string;
  category: ServiceCategory;
  authorityServiceCode?: string | null;
  authority?: { id: Uuid; code: string | null; name: string; images?: string[] } | null;
  subCategory: string | null;
  description: string | null;
  timelineText: string | null;
  hsnSacCode: string;
  gstPercentage: DecimalStr;
  defaultApplicationFeeRupees: DecimalStr;
  defaultServiceChargeRupees: DecimalStr;
  defaultAssociateSplitPercentage: DecimalStr;
  isActive: boolean;
  images: string[];
  questionnaire: QuestionnaireQuestion[];
  requiredDocuments: string[];
  createdAt: IsoDate;
  updatedAt: IsoDate;
  stageRefundPolicy?: unknown;
}

export interface AdditionalChargeItem {
  id: string;
  name: string;
  amountRupees: string;
  gstPercentage?: string;
  hsnSacCode?: string;
  gstAmountRupees?: string;
  totalAmountRupees?: string;
}

export interface CreateServicePayload {
  name: string;
  category: ServiceCategory;
  authorityId?: Uuid | null;
  authorityServiceCode?: string | null;
  subCategory?: string;
  description?: string;
  timelineText?: string;
  defaultApplicationFeeRupees?: string;
  defaultServiceChargeRupees?: string;
  governmentFeeRupees?: string;
  associateGstPercentage?: string;
  associateHsnSacCode?: string;
  platformGstPercentage?: string;
  platformHsnSacCode?: string;
  gstPercentage?: string;
  defaultAssociateSplitPercentage?: string;
  hsnSacCode?: string;
  isStartsFrom?: boolean;
  startsFromRupees?: string | null;
  questionnaire?: QuestionnaireQuestion[];
  requiredDocuments?: string[];
  stageRefundPolicy?: Record<string, unknown>;
  additionalCharges?: AdditionalChargeItem[];
  images?: string[];
}

export interface UpdateServicePayload extends Partial<CreateServicePayload> {
  isActive?: boolean;
}

// ---- Platform config --------------------------------------
export interface PlatformConfig {
  id: number;
  platformFeePercentage: DecimalStr;
  platformFeeFlatPaise: BigIntStr;
  paymentGatewayFeeNonRefundable: boolean;
  supportPeriodDays: number;
  refundProcessingDays: number;
  invoiceAutoCancelDays: number;
  ecregiLegalName: string;
  ecregiGstin: string;
  ecregiPan: string;
  ecregiState: string;
  ecregiRegisteredAddress: string;
  defaultGstPercentage: DecimalStr;
  defaultTdsPercentageForPayout: DecimalStr;
  tdsSectionCode: string;
  updatedByAdminId: Uuid | null;
  updatedAt: IsoDate;
}

export type UpdatePlatformConfigPayload = Partial<{
  platformFeePercentage: string;
  platformFeeFlatPaise: string;
  paymentGatewayFeeNonRefundable: boolean;
  supportPeriodDays: number;
  refundProcessingDays: number;
  invoiceAutoCancelDays: number;
  ecregiLegalName: string;
  ecregiGstin: string;
  ecregiPan: string;
  ecregiState: string;
  ecregiRegisteredAddress: string;
  defaultGstPercentage: string;
  defaultTdsPercentageForPayout: string;
  tdsSectionCode: string;
}>;

// ---- Authorities ------------------------------------------
export interface AuthorityServiceRow {
  id: Uuid;
  authorityId: Uuid;
  code: string;
  name: string;
  isArchived?: boolean;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export interface AuthorityRow {
  id: Uuid;
  code: string | null;
  name: string;
  images?: string[];
  isArchived?: boolean;
  createdAt: IsoDate;
  updatedAt: IsoDate;
  services: AuthorityServiceRow[];
}

export interface InlineAuthorityService {
  code: string;
  name: string;
}

export interface CreateAuthorityPayload {
  name: string;
  code?: string | null;
  images?: string[];
  services?: InlineAuthorityService[];
}

export interface UpdateAuthorityPayload {
  name?: string;
  code?: string | null;
  images?: string[];
  isArchived?: boolean;
}

export interface CreateAuthorityServicePayload {
  authorityId: Uuid;
  code: string;
  name: string;
}

export interface UpdateAuthorityServicePayload {
  code?: string;
  name?: string;
  isArchived?: boolean;
}

export interface BulkImportAuthorityItem {
  authorityName: string;
  authorityCode?: string | null;
  serviceId: string;
  serviceName: string;
}

export interface BulkImportAuthoritiesPayload {
  items: BulkImportAuthorityItem[];
}

export interface BulkImportAuthoritiesResponse {
  success: boolean;
  totalProcessed: number;
  createdAuthoritiesCount: number;
  createdServicesCount: number;
  updatedServicesCount: number;
}

// ============================================================
// Audit Logs
// ============================================================

export interface AuditLogActor {
  id: Uuid;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: UserRole;
  adminRole: AdminRole | null;
}

export interface ResolvedEntityInfo {
  label: string;
  subtext?: string | null;
  type?: string | null;
}

export interface AuditLogRow {
  id: Uuid;
  actorUserId: Uuid | null;
  action: string;
  entityType: string | null;
  entityId: Uuid | null;
  entityLabel?: string | null;
  resolvedEntities?: Record<string, ResolvedEntityInfo>;
  before: unknown | null;
  after: unknown | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: IsoDate;
  actor: AuditLogActor | null;
}

export interface AuditLogPage {
  items: AuditLogRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AuditLogStats {
  totalLogs: number;
  todayLogs: number;
  uniqueActors: number;
  distinctActionsCount: number;
  topAction: string | null;
  topActorEmail: string | null;
}

export interface AuditLogMeta {
  actions: string[];
  entityTypes: string[];
}
